// Pure helpers behind the Access Control editor (src/pages/admin-access-control.tsx).
// Kept dependency-free (no React, no apiFetch) so the cycling / resolution /
// diffing logic can be unit tested without mounting anything.

export type Level = "none" | "read" | "write";
export type LevelOrInherit = Level | "inherit";

// Cell click order: an untouched cell inherits from the next rule up; the
// first click always makes the effective value explicit as "none" rather
// than jumping straight to a guess, then cycles read -> write -> back to
// inherit.
const LEVELS_CYCLE: LevelOrInherit[] = ["inherit", "none", "read", "write"];

export const cycleLevel = (current: LevelOrInherit): LevelOrInherit => {
    const idx = LEVELS_CYCLE.indexOf(current);
    return LEVELS_CYCLE[(idx + 1) % LEVELS_CYCLE.length];
};

export const levelGlyph = (level: LevelOrInherit): string => {
    switch (level) {
        case "none": return "N";
        case "read": return "R";
        case "write": return "W";
        default: return "–";
    }
};

export interface AccessKeyDef {
    key: string;
    label: string;
    group: string;
    kind: "menu" | "operation";
    floor: "Manager+" | "Admin" | null;
    defaults: Record<string, Level>;
    note: string | null;
}

export interface SubjectRuleSet {
    default: Record<string, Level>;
    warehouses: Record<string, Record<string, Level>>;
}

export interface AccessRules {
    positions: Record<string, SubjectRuleSet>;
    users: Record<string, SubjectRuleSet>;
}

export type EffectiveSource =
    | "user@warehouse"
    | "user"
    | "position@warehouse"
    | "position"
    | "default"
    | "unseeded"
    | "admin-locked";

export interface EffectiveResult {
    level: Level;
    source: EffectiveSource;
}

export interface EffectiveArgs {
    keyDef: AccessKeyDef;
    rules: AccessRules;
    position: string;
    warehouse?: string | null;
    uid?: string | null;
}

// Resolves the level that would actually apply for a position/warehouse/user
// combination, walking the same precedence order as the backend: user +
// warehouse > user default > position + warehouse > position default > the
// code-level default shipped with the key definition. Admin is a hard floor
// — always fully enabled, never editable — matching authenticateLogin's
// existing behaviour so the matrix cannot be used to lock out the one
// position that must always get in.
export const effectiveLevel = ({ keyDef, rules, position, warehouse, uid }: EffectiveArgs): EffectiveResult => {
    if (position === "Admin") return { level: "write", source: "admin-locked" };

    if (uid) {
        const userRules = rules.users[uid];
        if (warehouse && userRules?.warehouses?.[warehouse]?.[keyDef.key] !== undefined) {
            return { level: userRules.warehouses[warehouse][keyDef.key], source: "user@warehouse" };
        }
        if (userRules?.default?.[keyDef.key] !== undefined) {
            return { level: userRules.default[keyDef.key], source: "user" };
        }
    }

    const posRules = rules.positions[position];
    if (warehouse && posRules?.warehouses?.[warehouse]?.[keyDef.key] !== undefined) {
        return { level: posRules.warehouses[warehouse][keyDef.key], source: "position@warehouse" };
    }
    if (posRules?.default?.[keyDef.key] !== undefined) {
        return { level: posRules.default[keyDef.key], source: "position" };
    }

    const codeDefault = keyDef.defaults[position];
    if (codeDefault !== undefined) return { level: codeDefault, source: "default" };

    return { level: "none", source: "unseeded" };
};

export interface StoredArgs {
    rules: AccessRules;
    subjectType: "position" | "user";
    subjectId: string;
    /** null = the Default tab / Default column for this subject. */
    warehouse: string | null;
    key: string;
}

// The value actually stored for this exact subject/scope cell, or
// "inherit" when nothing is stored there (the effective value then falls
// through to the next rule up — see effectiveLevel).
export const storedLevel = ({ rules, subjectType, subjectId, warehouse, key }: StoredArgs): LevelOrInherit => {
    const set = subjectType === "position" ? rules.positions[subjectId] : rules.users[subjectId];
    if (!set) return "inherit";
    if (warehouse) return set.warehouses?.[warehouse]?.[key] ?? "inherit";
    return set.default?.[key] ?? "inherit";
};

// Cell identity used as the key of the in-progress edits map.   is used
// as the separator (rather than ":") because neither access keys nor
// warehouse names can contain it, whereas both can legitimately contain
// dots or colons.
export const cellId = (subjectType: "position" | "user", subjectId: string, warehouse: string | null, key: string): string =>
    `${subjectType} ${subjectId} ${warehouse ?? ""} ${key}`;

export interface ParsedCellId {
    subjectType: "position" | "user";
    subjectId: string;
    warehouse: string | null;
    key: string;
}

export const parseCellId = (id: string): ParsedCellId => {
    const [subjectType, subjectId, warehouseRaw, key] = id.split(" ");
    return {
        subjectType: subjectType as "position" | "user",
        subjectId,
        warehouse: warehouseRaw === "" ? null : warehouseRaw,
        key,
    };
};

const cloneRuleSet = (s: SubjectRuleSet): SubjectRuleSet => ({
    default: { ...s.default },
    warehouses: Object.fromEntries(Object.entries(s.warehouses).map(([w, m]) => [w, { ...m }])),
});

// Overlays a sparse map of pending edits (cellId -> new value, "inherit"
// meaning "clear the stored override") onto a rules object, returning a new
// object. Used so the matrix can show what would be effective if the
// in-progress edits were saved, without mutating the loaded config.
export const applyEdits = (rules: AccessRules, edits: Record<string, LevelOrInherit>): AccessRules => {
    const next: AccessRules = {
        positions: Object.fromEntries(Object.entries(rules.positions).map(([k, v]) => [k, cloneRuleSet(v)])),
        users: Object.fromEntries(Object.entries(rules.users).map(([k, v]) => [k, cloneRuleSet(v)])),
    };
    for (const [id, level] of Object.entries(edits)) {
        const { subjectType, subjectId, warehouse, key } = parseCellId(id);
        const bucket = subjectType === "position" ? next.positions : next.users;
        if (!bucket[subjectId]) bucket[subjectId] = { default: {}, warehouses: {} };
        const set = bucket[subjectId];
        if (warehouse) {
            if (!set.warehouses[warehouse]) set.warehouses[warehouse] = {};
            if (level === "inherit") delete set.warehouses[warehouse][key];
            else set.warehouses[warehouse][key] = level;
        } else {
            if (level === "inherit") delete set.default[key];
            else set.default[key] = level;
        }
    }
    return next;
};

export interface PendingChange {
    subject_type: "position" | "user";
    subject_id: string;
    warehouse: string | null;
    key: string;
    level: LevelOrInherit;
}

// Builds the minimal PUT /access-control/rules changes array: only cells
// whose edited value differs from what is actually stored (not the
// resolved/effective value) go out, so cycling a cell back to its original
// value — or four clicks landing back on "inherit" — is a no-op save, and
// an untouched cell is never written back.
export const diffChanges = (
    original: Record<string, LevelOrInherit>,
    edited: Record<string, LevelOrInherit>,
): PendingChange[] => {
    const changes: PendingChange[] = [];
    for (const id of Object.keys(edited)) {
        const before = original[id] ?? "inherit";
        const after = edited[id];
        if (before === after) continue;
        const { subjectType, subjectId, warehouse, key } = parseCellId(id);
        changes.push({ subject_type: subjectType, subject_id: subjectId, warehouse, key, level: after });
    }
    return changes;
};

/** The area menu key that gates `key` (E8): `admin` for admin.*, `app` for everything else,
 *  null for the two area keys themselves. Mirrors areaMenuKey() in the BE's keys.js. */
export const areaMenuKey = (key: string): "app" | "admin" | null => {
    if (key === "app" || key === "admin") return null;
    return key.startsWith("admin.") ? "admin" : "app";
};

/** True when `key` is inert for this subject because its area resolves to none. Admin is never gated. */
export const isGated = (
    key: string,
    keys: AccessKeyDef[],
    args: Omit<EffectiveArgs, "keyDef">,
): boolean => {
    if (args.position === "Admin") return false;
    const area = areaMenuKey(key);
    if (!area) return false;
    const areaDef = keys.find(k => k.key === area);
    if (!areaDef) return false;
    return effectiveLevel({ ...args, keyDef: areaDef }).level === "none";
};
